import React from 'react';
import { Link } from 'react-router-dom';
import { AwardIcon, BadgeCheckIcon, ShieldAlertIcon, UserPlusIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { ReportControls } from '../trust/ReportControls';
import { useSocial } from '../../contexts/SocialContext';

/**
 * Clubs and organisations as first-class entities, with the verification
 * distinction stated plainly: verified means checked against the institution's
 * own register, and anything else says so rather than staying silent.
 */
export function ClubsPanel() {
  const {
    clubs,
    toggleClub
  } = useSocial();
  return <div className="space-y-5" data-id="element-5483" data-mp="1umo29tc38o2o">
      {clubs.map(club => <Panel key={club.id} title={club.name} description={club.description} action={club.verified ? <Badge tone="ok" icon={<BadgeCheckIcon className="h-4 w-4" data-id="element-5486" data-mp="12529rwa5ie72" />} data-id="element-5485" data-mp="125licmdwvq8f">
                Campuzon Verified
              </Badge> : <Badge tone="warn" icon={<ShieldAlertIcon className="h-4 w-4" data-id="element-5488" data-mp="2i18mk2evj2ct" />} data-id="element-5487" data-mp="2n2fpno62ndd5">
                Not verified
              </Badge>} data-id="element-5484" data-mp="1uo91hw9ckv6v">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,300px)]" data-id="element-5489" data-mp="2d9gsvj0f8gig">
            <div data-id="element-5490" data-mp="299q2hget42eg">
              <dl className="grid gap-3 sm:grid-cols-3" data-id="element-5491" data-mp="2985anyjvmxka">
                <div data-id="element-5492" data-mp="249k9nwwsw9us">
                  <dt className="text-body-sm text-ink-muted" data-id="element-5493" data-mp="24a3o33n8sg42">Members</dt>
                  <dd className="cz-num text-body-lg font-semibold text-ink" data-id="element-5494" data-mp="1zb0kzoqtojyq">
                    {club.members.toLocaleString()}
                  </dd>
                </div>
                <div data-id="element-5495" data-mp="1udqppsgr5e2z">
                  <dt className="text-body-sm text-ink-muted" data-id="element-5496" data-mp="1qac50ohvc9j7">Category</dt>
                  <dd className="text-body text-ink" data-id="element-5497" data-mp="1qc5ba2okkrqo">{club.category}</dd>
                </div>
                <div data-id="element-5498" data-mp="3onktv95ff4x0">
                  <dt className="text-body-sm text-ink-muted" data-id="element-5499" data-mp="3ko3mga6fvxp7">Your role</dt>
                  <dd className="text-body text-ink" data-id="element-5500" data-mp="3klz8t0iqnkbq">{club.myRole ?? 'Not a member'}</dd>
                </div>
              </dl>

              <p className="mt-3 max-w-[74ch] text-body-sm text-ink-muted" data-id="element-5501" data-mp="1mrv7lw9mctu4">
                {club.verificationNote}
              </p>

              <h3 className="mt-4 text-body font-semibold text-ink" data-id="element-5502" data-mp="1921hlamdo3it">Leadership</h3>
              <ul className="mt-2 space-y-1.5" data-id="element-5503" data-mp="1d1k3o9pxqkn8">
                {club.officers.map(officer => <li key={officer.handle} className="text-body text-ink" data-id="element-5504" data-mp="20ro64dre0ecn">
                    <Link to={`/community/people/${officer.handle}`} className="font-medium hover:underline" data-id="element-5505" data-mp="25qr0n49qr26d">
                      {officer.name}
                    </Link>
                    <span className="text-ink-muted" data-id="element-5506" data-mp="1wozujybagtbz"> — {officer.role}</span>
                  </li>)}
              </ul>

              {club.achievements.length > 0 && <>
                  <h3 className="mt-4 text-body font-semibold text-ink" data-id="element-5507" data-mp="lcqpsin09jgt">Recognition</h3>
                  <ul className="mt-2 space-y-1.5" data-id="element-5508" data-mp="csu3todddf4l">
                    {club.achievements.map(achievement => <li key={achievement} className="flex items-start gap-2 text-body-sm text-ink" data-id="element-5509" data-mp="8s0j13v84gfx">
                        <AwardIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" data-id="element-5510" data-mp="hu9qtlz3xvpm" />
                        {achievement}
                      </li>)}
                  </ul>
                </>}

              <div className="mt-5 flex flex-wrap items-center gap-2" data-id="element-5511" data-mp="3l7vqawtr50xh">
                <Button variant={club.joined ? 'secondary' : 'primary'} size="sm" icon={<UserPlusIcon className="h-4 w-4" data-id="element-5513" data-mp="3v61p35k9hqy4" />} onClick={() => toggleClub(club.id)} data-id="element-5512" data-mp="3v1myfs01zf3">
                  {club.joined ? 'Leave club' : 'Request to join'}
                </Button>
                <Link to="/community" className="text-body-sm font-medium text-brand-700 hover:underline" data-id="element-5514" data-mp="25s2eh4tm76hj">
                  Club events and discussions
                </Link>
                <div className="ml-auto" data-id="element-5515" data-mp="2esibpwjrlhdy">
                  <ReportControls subject={club.name} surface="group" data-id="element-5516" data-mp="2ergyblfa0it2" />
                </div>
              </div>
            </div>

            {club.myRole === 'Member' || club.membershipRequests > 0 ? <div className="rounded-md border border-line bg-canvas p-4" data-id="element-5517" data-mp="2oko2x426zus3">
                <h3 className="text-body font-semibold text-ink" data-id="element-5518" data-mp="2tg3efpcq9elc">Club administration</h3>
                {club.membershipRequests > 0 ? <p className="mt-1.5 text-body-sm text-ink" data-id="element-5519" data-mp="32icnsbw9f1x5">
                    {club.membershipRequests} membership requests are waiting. Only officers can
                    accept them — your role here is {club.myRole ?? 'not a member'}.
                  </p> : <p className="mt-1.5 text-body-sm text-ink" data-id="element-5520" data-mp="1864ypf6mdf9a">
                    Nothing waiting. Officers manage members, announcements, events and attendance
                    from here.
                  </p>}
                <p className="mt-2 text-caption text-ink-subtle" data-id="element-5521" data-mp="quhmsqz7o5x0">
                  Club moderation is scoped to this club. It gives no access to campus-wide content
                  or to anyone's private messages.
                </p>
              </div> : <div className="rounded-md border border-line bg-canvas p-4" data-id="element-5522" data-mp="1ekkayw008rqc">
                <h3 className="text-body font-semibold text-ink" data-id="element-5523" data-mp="1eihbllcypm8y">Membership</h3>
                <p className="mt-1.5 text-body-sm text-ink" data-id="element-5524" data-mp="1ahf9mwrnuw3a">
                  Joining is a request the club's officers approve. Your programme and level are
                  shared with them; nothing else is.
                </p>
              </div>}
          </div>
        </Panel>)}
    </div>;
}